import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../components/ui';
import { Button, Card, Input, Spinner, Badge } from '../../components/ui';
import { 
  Send, CheckCircle, AlertCircle, Users, FileText, 
  CreditCard, ArrowRight, Zap, Globe, Clock, 
  Sparkles, BarChart3, MessageSquare, Shield,
  ChevronRight, Wallet, UserCheck, TrendingUp
} from 'lucide-react';
import './SendCampaign.css';

const SendCampaign = () => {
  const [phonebooks, setPhonebooks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    phonebookId: '',
    templateId: '',
    jobName: '',
    sendToAllOptedIn: true 
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const { request } = useApi();
  const { success, error: toastError } = useToast();
  const navigate = useNavigate();

  // Simple fetch 
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pbData, tplData] = await Promise.all([
          request('get', '/api/phonebooks').catch(() => []),
          request('get', '/api/templates').catch(() => [])
        ]);
        setPhonebooks(pbData || []);
        const approvedTemplates = (tplData || []).filter(t => t.status === 'APPROVED');
        setTemplates(approvedTemplates);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setFetching(false);
      }
    };
    
     fetchData();
  }, []);

  const selectedPhonebook = phonebooks.find(p => p.id === form.phonebookId);
  const selectedTemplate = templates.find(t => t.id === form.templateId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);

    try {
      const response = await request('post', '/api/send/bulk', form);
      setResult(response);
      success('Campaign started successfully!');
      setForm({ phonebookId: '', templateId: '', jobName: '', sendToAllOptedIn: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send campaign');
      toastError(err.response?.data?.error || 'Failed to send campaign');
    } finally {
      setLoading(false);
    }
  };

  const getPreviewText = (content) => {
    return content.replace(/\{\{(\d+)\}\}/g, (_, n) => `[var${n}]`);
  };

  // If still fetching, show loading 
  if (fetching) {
    return (
      <div className="send-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  const hasPhonebooks = phonebooks.length > 0;
  const hasApprovedTemplates = templates.length > 0;
  const canSend = hasPhonebooks && hasApprovedTemplates && form.phonebookId && form.templateId && form.jobName;

  return (
    <div className="send-campaign-page">
      {/* Rest of your JSX - same as before */}
      <div className="send-header">
        <div>
          <h1 className="send-title">Send Campaign</h1>
          <p className="send-subtitle">Send bulk WhatsApp messages to opted-in contacts</p>
        </div>
        <div className="send-header-info">
          <div className="header-stat">
            <span className="header-stat-value">{phonebooks.reduce((sum, pb) => sum + (pb.optedInContacts || 0), 0)}</span>
            <span className="header-stat-label">Total Opted-in</span>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      {(!hasPhonebooks || !hasApprovedTemplates) && (
        <div className="warning-banner">
          <AlertCircle size={18} />
          <div>
            <strong>Setup required</strong>
            <p>
              {!hasPhonebooks && !hasApprovedTemplates && 'You need to create phonebooks and approved templates first.'}
              {!hasPhonebooks && hasApprovedTemplates && 'You need to create a phonebook first.'}
              {hasPhonebooks && !hasApprovedTemplates && 'You need to create and approve a template first.'}
            </p>
          </div>
          <div className="warning-actions">
            {!hasPhonebooks && (
              <Button size="sm" onClick={() => navigate('/phonebooks')}>
                Create Phonebook
              </Button>
            )}
            {!hasApprovedTemplates && (
              <Button size="sm" variant="outline" onClick={() => navigate('/templates')}>
                Create Template
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="send-layout">
        {/* Form Card */}
        <Card className="send-form-card" padding="lg">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Campaign Name</label>
              <Input
                value={form.jobName}
                onChange={(e) => setForm({ ...form, jobName: e.target.value })}
                placeholder="e.g., Payment Reminder June"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Select Phonebook</label>
              <div className="select-wrapper">
                <Users size={18} className="select-icon" />
                <select
                  className="form-select"
                  value={form.phonebookId}
                  onChange={(e) => setForm({ ...form, phonebookId: e.target.value })}
                  required
                  disabled={!hasPhonebooks}
                >
                  <option value="">Choose a phonebook...</option>
                  {phonebooks.map(pb => (
                    <option key={pb.id} value={pb.id}>
                      {pb.name} ({pb.optedInContacts} opted-in contacts)
                    </option>
                  ))}
                </select>
              </div>
              {!hasPhonebooks && (
                <div className="form-hint error">
                  No phonebooks available. Create one first.
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Select Template</label>
              <div className="select-wrapper">
                <FileText size={18} className="select-icon" />
                <select
                  className="form-select"
                  value={form.templateId}
                  onChange={(e) => setForm({ ...form, templateId: e.target.value })}
                  required
                  disabled={!hasApprovedTemplates}
                >
                  <option value="">Choose a template...</option>
                  {templates.map(tpl => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name} ({tpl.category})
                    </option>
                  ))}
                </select>
              </div>
              {!hasApprovedTemplates && (
                <div className="form-hint error">
                  No approved templates available. Create and approve a template first.
                </div>
              )}
            </div>

            {selectedTemplate && (
              <div className="template-preview-card">
                <div className="preview-header">
                  <Globe size={14} />
                  <span>Message Preview</span>
                </div>
                <div className="preview-bubble">
                  {getPreviewText(selectedTemplate.content)}
                </div>
                <div className="preview-note">
                  <Zap size={12} />
                  <span>Variables will be replaced with contact data</span>
                </div>
              </div>
            )}

            {error && (
              <div className="alert-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {result && (
              <div className="alert-success">
                <CheckCircle size={16} />
                <div>
                  <strong>Campaign started successfully!</strong>
                  <p>Job ID: <span className="mono">{result.jobId}</span></p>
                  <p>Recipients: {result.totalRecipients} contacts</p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              disabled={!canSend || loading}
              className="send-btn"
            >
              <Send size={16} />
              {loading ? 'Sending...' : 'Send Campaign'}
            </Button>
          </form>
        </Card>

        {/* Sidebar */}
        <div className="send-sidebar">
          {/* Cost Card */}
          {selectedPhonebook && (
            <Card className="cost-card" padding="lg">
              <div className="cost-header">
                <CreditCard size={20} />
                <h3>Cost Estimate</h3>
              </div>
              <div className="cost-items">
                <div className="cost-item">
                  <span>Phonebook</span>
                  <strong>{selectedPhonebook.name}</strong>
                </div>
                <div className="cost-item">
                  <span>Opted-in contacts</span>
                  <strong>{selectedPhonebook.optedInContacts}</strong>
                </div>
                <div className="cost-item highlight">
                  <span>Credits needed</span>
                  <strong>{selectedPhonebook.optedInContacts}</strong>
                </div>
              </div>
              <div className="cost-note">
                <AlertCircle size={14} />
                <span>Make sure you have enough credits before sending.</span>
              </div>
              <Button 
                variant="outline" 
                fullWidth 
                size="sm"
                onClick={() => navigate('/billing')}
              >
                <Wallet size={14} />
                Buy Credits
              </Button>
            </Card>
          )}

          {/* Tips Card */}
          <Card className="tips-card" padding="lg">
            <div className="tips-header">
              <Zap size={20} />
              <h3>Pro Tips</h3>
            </div>
            <ul className="tips-list">
              <li>
                <div className="tip-icon">1</div>
                <div>
                  <strong>Use personalization</strong>
                  <p>Include variables to make messages personal</p>
                </div>
              </li>
              <li>
                <div className="tip-icon">2</div>
                <div>
                  <strong>Test before sending</strong>
                  <p>Preview messages before sending to all</p>
                </div>
              </li>
              <li>
                <div className="tip-icon">3</div>
                <div>
                  <strong>Monitor delivery</strong>
                  <p>Check Reports page to track performance</p>
                </div>
              </li>
              <li>
                <div className="tip-icon">4</div>
                <div>
                  <strong>Check credits</strong>
                  <p>Ensure you have enough credits for your campaign</p>
                </div>
              </li>
            </ul>
          </Card>

          {/* Quick Stats Card */}
          <Card className="stats-card" padding="lg">
            <div className="stats-header">
              <BarChart3 size={20} />
              <h3>Quick Stats</h3>
            </div>
            <div className="quick-stats">
              <div className="quick-stat">
                <div className="quick-stat-value">{phonebooks.length}</div>
                <div className="quick-stat-label">Phonebooks</div>
              </div>
              <div className="quick-stat">
                <div className="quick-stat-value">{templates.length}</div>
                <div className="quick-stat-label">Templates</div>
              </div>
              <div className="quick-stat">
                <div className="quick-stat-value">{phonebooks.reduce((sum, pb) => sum + (pb.optedInContacts || 0), 0)}</div>
                <div className="quick-stat-label">Total Opted-in</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SendCampaign;